import type { Post } from '../types';
import { PLATFORM_ICONS } from '../constants';

interface PostListProps {
  posts: Post[];
  onPostClick: (post: Post, index: number) => void;
  compact?: boolean; // For sidebar in combined view
  isExpanded: boolean;
}

function getStatusColor(status: string): string {
  switch(status) {
    case 'draft': return 'bg-gray-400';
    case 'approved': return 'bg-green-500';
    case 'scheduled': return 'bg-blue-500';
    case 'posted': return 'bg-purple-500';
    default: return 'bg-gray-300';
  }
}

function getStatusLabel(status: string): string {
  switch(status) {
    case 'draft': return 'Draft';
    case 'approved': return 'Approved';
    case 'scheduled': return 'Scheduled';
    case 'posted': return 'Posted';
    default: return 'Unknown';
  }
}

function groupPostsByDate(posts: Post[]): Map<string, Post[]> {
  const grouped = new Map<string, Post[]>();

  posts.forEach(post => {
    const existing = grouped.get(post.date) || [];
    grouped.set(post.date, [...existing, post]);
  });

  // Sort posts within each date by time
  grouped.forEach((datePosts, date) => {
    grouped.set(date, datePosts.sort((a, b) => a.time.localeCompare(b.time)));
  });

  return grouped;
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());

    if (dateOnly.getTime() === todayOnly.getTime()) {
      return 'Today';
    } else if (dateOnly.getTime() === tomorrowOnly.getTime()) {
      return 'Tomorrow';
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

export function PostList({ posts, onPostClick, compact = false, isExpanded }: PostListProps) {
  const groupedPosts = groupPostsByDate(posts);
  const sortedDates = Array.from(groupedPosts.keys()).sort();

  if (posts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-muted-foreground text-sm">No posts scheduled</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {sortedDates.map(date => {
        const datePosts = groupedPosts.get(date) || [];

        return (
          <div key={date} className={isExpanded ? 'mb-4' : 'mb-2'}>
            <div className={`sticky top-0 bg-muted/80 backdrop-blur-sm border-b ${isExpanded ? 'pl-3 pr-2 py-2' : 'pl-2 pr-1 py-1'}`}>
              <h3 className={`font-semibold text-foreground ${isExpanded ? 'text-sm' : 'text-xs'}`}>
                {formatDate(date)}
              </h3>
              <p className={`text-muted-foreground ${isExpanded ? 'text-xs' : 'text-[0.65rem]'}`}>
                {datePosts.length} {datePosts.length === 1 ? 'post' : 'posts'}
              </p>
            </div>

            <div className={`pl-2 pr-0 ${isExpanded ? 'space-y-1 py-1' : 'space-y-0.5 py-0.5'}`}>
              {datePosts.map((post, idx) => {
                const globalIndex = posts.indexOf(post);
                const iconSrc = PLATFORM_ICONS[post.platform];
                const firstImage = post.attachments?.find(a => a.type === 'image')?.url;

                return (
                  <button
                    key={`${post.date}-${post.time}-${idx}`}
                    onClick={() => onPostClick(post, globalIndex)}
                    className={`w-full flex items-start gap-2 rounded-l-md hover:bg-accent transition-colors text-left ${isExpanded ? 'pl-2 pr-1 py-2' : 'pl-1.5 pr-0.5 py-1'}`}
                  >
                    {firstImage && (
                      <div className={
                        compact
                          ? (isExpanded ? "w-12 aspect-square" : "w-10 aspect-square") +
                            " flex-shrink-0 bg-muted/20 rounded overflow-hidden"
                          : (isExpanded ? "w-24 aspect-square" : "w-20 aspect-square") +
                            " flex-shrink-0 bg-muted/20 rounded overflow-hidden"
                      }>
                        <img
                          src={firstImage}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className={`flex items-center gap-1 ${isExpanded ? 'mb-1' : 'mb-0.5'}`}>
                        {iconSrc && (
                          <img src={iconSrc} alt="" className={isExpanded ? 'w-4 h-4 flex-shrink-0' : 'w-3 h-3 flex-shrink-0'} />
                        )}
                        <span className={`font-medium text-foreground truncate ${isExpanded ? 'text-sm' : 'text-xs'}`}>
                          {post.topic}
                        </span>
                      </div>

                      <div className={`text-muted-foreground ${isExpanded ? 'text-xs mb-1' : 'text-[0.65rem] mb-0.5'}`}>
                        {post.time}
                      </div>

                      <div className={compact ? `text-muted-foreground truncate ${isExpanded ? 'text-xs' : 'text-[0.65rem]'}` : `text-muted-foreground line-clamp-3 ${isExpanded ? 'text-xs' : 'text-[0.65rem]'}`}>
                        {post.text}
                      </div>

                      {post.status && (
                        <div className={`flex items-center gap-1.5 ${isExpanded ? 'mt-1.5' : 'mt-1'}`}>
                          <div className={`rounded-full ${getStatusColor(post.status)} ${isExpanded ? 'w-2 h-2' : 'w-1.5 h-1.5'}`} />
                          <span className={`text-muted-foreground ${isExpanded ? 'text-xs' : 'text-[0.65rem]'}`}>
                            {getStatusLabel(post.status)}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
